import { useState, useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useIsMobile } from "@/hooks/use-mobile";
import { apiFetch, API } from "@/utils/api";
import AiChatPanel from "@/components/AiChatPanel";

export default function AiChat() {
  const { pathname } = useLocation();
  const overActionsBar = pathname.startsWith("/facturas");
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat"); // "chat" | "history"
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  // La conversación activa sobrevive a cerrar/abrir el panel: el estado vive
  // aquí (AiChat queda montado en Layout) y al abrir ya NO se resetea.
  const [conversationId, setConversationId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  const messagesEndRef = useRef(null);
  // Época de conversación (patrón linkSeq de MobileInvoiceSheet): cambiar de
  // hilo / nueva conversación / borrar la activa la incrementa. Una respuesta
  // pendiente de una época anterior se DESCARTA al llegar (el backend ya la
  // persistió en su hilo; se verá al volver a él) — sin esto, la respuesta de
  // un hilo aterrizaba en el hilo que el usuario estuviera viendo.
  const convEpoch = useRef(0);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open, view]);

  const sendMessage = async (text) => {
    if (!text.trim()) return;
    const epoch = convEpoch.current;
    const userMsg = { role: "user", content: text };
    setMessages((prev) => [...prev, userMsg]);
    setInput("");
    setLoading(true);

    try {
      const res = await apiFetch(`${API}/ai/chat`, {
        method: "POST",
        body: JSON.stringify({
          question: text,
          conversation_id: conversationId,
        }),
      });
      if (convEpoch.current !== epoch) return; // el usuario cambió de hilo
      if (res.ok) {
        const data = await res.json();
        setConversationId(data.conversation_id);
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: data.answer },
        ]);
      } else {
        const err = await res.json().catch(() => ({}));
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: err.detail || "No pude procesar tu pregunta. Verifica que la IA esté configurada.",
          },
        ]);
      }
    } catch (e) {
      if (convEpoch.current !== epoch) return;
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: "Error de conexión con el servidor." },
      ]);
    } finally {
      if (convEpoch.current === epoch) setLoading(false);
    }
  };

  const loadConversations = async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const res = await apiFetch(`${API}/ai/conversations`);
      if (res.ok) {
        setConversations(await res.json());
      } else {
        setHistoryError("No se pudieron cargar las conversaciones.");
      }
    } catch (e) {
      setHistoryError("Error de conexión con el servidor.");
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistory = () => {
    if (view === "history") {
      setView("chat");
    } else {
      setView("history");
      loadConversations();
    }
  };

  const newConversation = () => {
    convEpoch.current += 1;
    setMessages([]);
    setConversationId(null);
    setInput("");
    setLoading(false);
    setView("chat");
  };

  const selectConversation = async (id) => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const res = await apiFetch(`${API}/ai/conversations/${id}`);
      if (res.ok) {
        const data = await res.json();
        convEpoch.current += 1;
        setMessages(
          (data.messages ?? []).map((m) => ({ role: m.role, content: m.content }))
        );
        setConversationId(data.conversation_id ?? id);
        setLoading(false);
        setView("chat");
      } else {
        setHistoryError("No se pudo cargar la conversación.");
      }
    } catch (e) {
      setHistoryError("Error de conexión con el servidor.");
    } finally {
      setHistoryLoading(false);
    }
  };

  const deleteConversation = async (id) => {
    if (!window.confirm("¿Borrar esta conversación? No se puede deshacer.")) return;
    try {
      const res = await apiFetch(`${API}/ai/conversations/${id}`, {
        method: "DELETE",
      });
      if (res.ok) {
        if (id === conversationId) {
          convEpoch.current += 1;
          setMessages([]);
          setConversationId(null);
          setLoading(false);
        }
        loadConversations();
      } else {
        setHistoryError("No se pudo borrar la conversación.");
      }
    } catch (e) {
      setHistoryError("Error de conexión con el servidor.");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className={`fixed ${overActionsBar ? "bottom-[8.75rem]" : "bottom-24"} lg:bottom-5 right-5 z-50 w-12 h-12 rounded-full bg-slate-900 text-white shadow-lg hover:bg-slate-800 transition-all flex items-center justify-center hover:scale-105`}
        title="CFO Asistente IA"
      >
        <Sparkles className="w-5 h-5" />
      </button>
    );
  }

  const panel = (
    <AiChatPanel
      view={view}
      messages={messages}
      loading={loading}
      input={input}
      conversations={conversations}
      historyLoading={historyLoading}
      historyError={historyError}
      activeConversationId={conversationId}
      messagesEndRef={messagesEndRef}
      onInputChange={setInput}
      onSend={() => sendMessage(input)}
      onSendSuggested={sendMessage}
      onKeyDown={handleKeyDown}
      onToggleHistory={toggleHistory}
      onNewConversation={newConversation}
      onSelectConversation={selectConversation}
      onDeleteConversation={deleteConversation}
      onClose={() => setOpen(false)}
      mobile={isMobile}
    />
  );

  if (isMobile) {
    // Hoja inferior móvil (molde MovementSheet): header shrink-0 + cuerpo
    // scrollable grow + input fijo shrink-0. El contenido es el mismo panel.
    return (
      <Sheet open onOpenChange={(o) => !o && setOpen(false)}>
        <SheetContent
          side="bottom"
          data-testid="aichat-sheet"
          className="max-h-[85%] p-0 gap-0 rounded-t-2xl flex flex-col overflow-hidden [&>button]:hidden"
        >
          <SheetTitle className="sr-only">CFO Asistente IA</SheetTitle>
          <SheetDescription className="sr-only">
            Chat con el asistente CFO sobre tus finanzas
          </SheetDescription>
          {panel}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div className="fixed bottom-24 lg:bottom-5 right-5 z-50 w-[400px] h-[500px] bg-white border border-slate-200 rounded-xl shadow-2xl flex flex-col overflow-hidden">
      {panel}
    </div>
  );
}
